// Date utility functions for Streak Spark calendar functionality
import { DayOfWeek } from '../types';

// Get the day of the week as a number (0 = Sunday, 1 = Monday, etc.)
export const getDayOfWeekNumber = (date: Date): number => {
  return date.getDay();
};

// Get the day of the week as a DayOfWeek string
export const getDayOfWeekString = (date: Date): DayOfWeek => {
  const days: DayOfWeek[] = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
  return days[getDayOfWeekNumber(date)];
};

// Check if a habit is active for a given date
export const isHabitActiveForDate = (habitActiveDays: DayOfWeek[] | undefined, date: Date): boolean => {
  if (!habitActiveDays || habitActiveDays.length === 0) {
    return true; // If no active days specified, habit is active every day
  }

  const dayOfWeek = getDayOfWeekString(date);
  return habitActiveDays.includes(dayOfWeek);
};

/**
 * Format date to YYYY-MM-DD string
 */
export const formatDateToString = (date: Date): string => {
  const year = date.getFullYear();
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const day = date.getDate().toString().padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * Parse date string (YYYY-MM-DD or ISO string) to Date object
 * Always returns date in local time to match how dates are stored/formatted
 */
export const parseDateString = (dateString: string): Date => {
  if (!dateString) {
    return new Date();
  }
  
  // Handle ISO string format (e.g., "2024-01-15T00:00:00.000Z")
  if (dateString.includes('T')) {
    // Extract YYYY-MM-DD from ISO string and parse as local date
    const datePart = dateString.split('T')[0];
    const [year, month, day] = datePart.split('-').map(Number);
    if (isNaN(year) || isNaN(month) || isNaN(day)) {
      return new Date();
    }
    return new Date(year, month - 1, day);
  }
  
  // Handle YYYY-MM-DD format
  const [year, month, day] = dateString.split('-').map(Number);
  if (isNaN(year) || isNaN(month) || isNaN(day)) {
    return new Date();
  }
  return new Date(year, month - 1, day);
};

/**
 * Get today's date as YYYY-MM-DD string
 */
export const getTodayString = (): string => {
  return formatDateToString(new Date());
};

/**
 * Check if date string is today
 */
export const isToday = (dateString: string): boolean => {
  return dateString === getTodayString();
};

/**
 * Get month name from date
 */
export const getMonthName = (date: Date): string => {
  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];
  return months[date.getMonth()];
};

/**
 * Get short day names for calendar header
 */
export const getDayNames = (): string[] => {
  return ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
};

/**
 * Get all dates for a month view (including previous/next month dates for complete grid)
 * Only shows complete weeks needed to display the full month
 */
export const getMonthDates = (year: number, month: number): Date[] => {
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0); // Last day of the month
  const startDate = new Date(firstDay);
  
  // Go back to start of week (Sunday)
  const dayOfWeek = firstDay.getDay();
  startDate.setDate(1 - dayOfWeek);
  
  const dates: Date[] = [];
  const currentDate = new Date(startDate);
  
  // Calculate exactly how many days we need:
  // Days from start of week to end of month + days to complete the last week
  const lastDayOfWeek = lastDay.getDay();
  const daysToEndOfMonth = dayOfWeek + lastDay.getDate();
  const daysToCompleteLastWeek = lastDayOfWeek === 6 ? 0 : (6 - lastDayOfWeek);
  const totalDays = daysToEndOfMonth + daysToCompleteLastWeek;
  
  // Generate dates for the calculated number of days
  for (let i = 0; i < totalDays; i++) {
    dates.push(new Date(currentDate));
    currentDate.setDate(currentDate.getDate() + 1);
  }
  
  return dates;
};

/**
 * Check if date is in the current month
 */
export const isDateInMonth = (date: Date, month: number, year: number): boolean => {
  return date.getMonth() === month && date.getFullYear() === year;
};

/**
 * Navigate to previous month
 */
export const navigateToPreviousMonth = (currentDate: Date): Date => {
  const newDate = new Date(currentDate);
  newDate.setMonth(newDate.getMonth() - 1);
  return newDate;
};

/**
 * Navigate to next month
 */
export const navigateToNextMonth = (currentDate: Date): Date => {
  const newDate = new Date(currentDate);
  newDate.setMonth(newDate.getMonth() + 1);
  return newDate;
};

/**
 * Get display text for month and year
 */
export const getMonthYearDisplay = (date: Date): string => {
  return `${getMonthName(date)} ${date.getFullYear()}`;
};

/**
 * Check if two date strings are the same
 */
export const isSameDate = (date1: string, date2: string): boolean => {
  return date1 === date2;
};

/**
 * Get day name from date
 */
export const getDayName = (date: Date): string => {
  const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  return days[date.getDay()];
};

/**
 * Format date for day view header (e.g., "March 15, 2024")
 */
export const formatDateForDayView = (date: Date): string => {
  const monthName = getMonthName(date);
  const day = date.getDate();
  /* const year = date.getFullYear(); */
  return `${monthName} ${day}`;
};

/**
 * Get day of week in DayOfWeek format (lowercase)
 */
export const getDayOfWeek = (date: Date): DayOfWeek => {
  const days: DayOfWeek[] = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
  return days[date.getDay()];
};

/**
 * Calculate the current streak for a habit based on completions
 * A streak is active if the most recent required completion date was completed
 * Shows current streak value until the streak is actually broken (not just when day starts)
 */
export const calculateCurrentStreak = (
  habit: { activeForDays?: DayOfWeek[]; createdAt: string },
  completions: { date: string; completed: boolean }[]
): number => {
  if (completions.length === 0) return 0;

  // Find the most recent date when the habit was active and completed
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  let checkDate = new Date(today);

  // Go backwards to find the most recent active date that was completed
  let lastCompletedDate: Date | null = null;
  for (let i = 0; i < 30; i++) { // Check up to 30 days back for completed dates
    if (isHabitActiveForDate(habit.activeForDays, checkDate)) {
      const dateString = formatDateToString(checkDate);
      const completion = completions.find(c => c.date === dateString);
      if (completion && completion.completed) {
        lastCompletedDate = new Date(checkDate);
        break;
      }
    }
    checkDate.setDate(checkDate.getDate() - 1);

    // Prevent going before habit creation
    const habitCreatedDate = new Date(habit.createdAt);
    habitCreatedDate.setHours(0, 0, 0, 0);
    if (checkDate < habitCreatedDate) {
      break;
    }
  }

  if (!lastCompletedDate) return 0; // No completed active dates found

  // Count consecutive completions backwards from the last completed date
  let streak = 0;
  let currentDate = new Date(lastCompletedDate);

  while (true) {
    const dateString = formatDateToString(currentDate);

    // Check if habit was active on this date
    if (!isHabitActiveForDate(habit.activeForDays, currentDate)) {
      // If habit wasn't active, continue to previous day
      currentDate.setDate(currentDate.getDate() - 1);
      continue;
    }

    // Check if completed on this date
    const completion = completions.find(c => c.date === dateString);
    if (!completion || !completion.completed) {
      // Gap found, streak breaks
      break;
    }

    streak++;

    // Move to previous day
    currentDate.setDate(currentDate.getDate() - 1);

    // Prevent infinite loop by checking against habit creation date
    const habitCreatedDate = new Date(habit.createdAt);
    habitCreatedDate.setHours(0, 0, 0, 0);
    if (currentDate < habitCreatedDate) {
      break;
    }
  }

  return streak;
};
